import { useMemo } from "react"

import * as turf from "@turf/turf"

import { sanitizeData } from "@l2r-front/l2r-geodata"
import { useTranslation } from "@l2r-front/l2r-i18n"
import { Source, GeoJSONSourceSpecification, useMapDispatchContext } from "@l2r-front/l2r-map"

import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { useRoadsideStateContext } from "../../contexts/RoadsideContext"
import { useRoadsideHomogenousZones } from "../../hooks/queries/useRoadsideHomogenousZones"

type RoadsideSourceProps = GeoJSONSourceSpecification & {
    id?: string,
    children?: React.ReactNode,
};

export function RoadsideSource(props: RoadsideSourceProps) {

    const { t } = useTranslation(I18N_NAMESPACE)
    const { setError: setMapError } = useMapDispatchContext()
    const { filters } = useRoadsideStateContext()

    const {
        ...sourceProps
    } = props

    const { data: homogenousZones } = useRoadsideHomogenousZones({
        as_geojson: true,
        ...filters?.conditions?.length && { condition: filters.conditions },
        ...filters?.type && { roadside_inventory_type__ancestor: filters.type },
    }, {
        onError: () => setMapError(true),
        errorSnackbarMessage: t(I18N_NAMESPACE, "containers.roadsideSource.error"),
    })

    // Need to copy id into properties because of that mapbox issue:
    // link:https://github.com/mapbox/mapbox-gl-js/issues/2716
    const sanitizedData = useMemo(() => {
        if (!homogenousZones) {
            return null
        }

        const homogenousFeatures = homogenousZones.features.map((feature) => {
            const positiveValue = feature.properties.inventory_states?.["+"]?.reduce((acc, state) => Math.max(acc, state.condition), 0) || 0
            const negativeValue = feature.properties.inventory_states?.["-"]?.reduce((acc, state) => Math.max(acc, state.condition), 0) || 0
            const value = Math.max(positiveValue, negativeValue)

            return {
                ...feature,
                properties: {
                    ...feature.properties,
                    condition: value,
                },
            }
        })

        return sanitizeData(turf.featureCollection(homogenousFeatures)) as GeoJSON.FeatureCollection

    }, [homogenousZones])

    return (
        <Source {...sourceProps} data={sanitizedData} tolerance={.001} />
    )
}